import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { WalkupWizard } from "@/components/test-entry/walkup-wizard";
import { Card, CardContent, PageHeader, Skeleton } from "@/components/ui";

interface WalkupEntrySearch {
  device_installation_id?: string;
}

// uuid-ish gate: canonical 8-4-4-4-12 hex (covers the v4 + v7 ids this codebase
// mints). A garbage / non-uuid id drops to undefined so the wizard simply opens
// unprefilled instead of firing a doomed fetch.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseUuid(v: unknown): string | undefined {
  return typeof v === "string" && UUID_RE.test(v) ? v : undefined;
}

export const Route = createFileRoute("/_authenticated/test-entry/new")({
  component: WalkupEntryPage,
  validateSearch: (s: Record<string, unknown>): WalkupEntrySearch => ({
    device_installation_id: parseUuid(s.device_installation_id),
  }),
});

/**
 * The page while the caller's permissions are still loading: its header over a
 * skeleton, so a cold load never reads as a refusal for the seconds the
 * permissions read takes.
 */
function PermissionsPending() {
  const { t } = useTranslation("test-entry");
  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t("composer.title")} backTo="/test-activity" />
      <Skeleton variant="card" />
    </div>
  );
}

/**
 * The permission guard for direct navigation: once the caller's permissions
 * have resolved without `form_submissions:create` (or failed to resolve), this
 * replaces the wizard, closing the bookmark/URL-typing bypass around the entry
 * CTAs' own gate.
 */
function NoPermissionGuard() {
  const { t } = useTranslation("test-entry");
  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t("composer.title")} backTo="/test-activity" />
      <Card>
        <CardContent>
          <p className="text-sm text-on-surface">{t("route.guard.noPermission")}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The walk-up rapid-entry route: the "Record test results" CTA on the
 * work-assignments list header lands here. All picker/resolution/
 * re-resolution logic lives in `WalkupWizard`; this route owns only the
 * permission gate, which denies by default and waits — header and skeleton —
 * while the permissions read is in flight.
 */
function WalkupEntryPage() {
  const { device_installation_id } = Route.useSearch();
  const { can, isLoading } = usePermissions();
  if (isLoading) {
    return <PermissionsPending />;
  }
  if (!can("form_submissions", "create")) {
    return <NoPermissionGuard />;
  }
  return <WalkupWizard initialDeviceInstallationId={device_installation_id} />;
}
